// TableCellContentUrl.tsx
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconLink } from "@tabler/icons-react";
import { FormInput } from "../../Inputs/FormInput";
import { PUrlEditor } from "../../PUrlEditor/PUrlEditor";
import { parseUrlField, isSafeHttpUrl } from "@/lib/utils/urlField";

type TableCellContentUrlProps = {
  onFocusAction?: () => void;
  onBlurAction?: () => void;
  isFilter?: boolean;
  border?: string;
  editable: boolean;
  value: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  viewOnly?: boolean;
};

export const TableCellContentUrl: React.FC<TableCellContentUrlProps> = ({
  isFilter,
  border = "outline-tertiary border focus-within:outline-focus",
  editable,
  value,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  onBlurAction,
  error,
  height = "h-9",
  surface = "",
  fontSize = "body-2-regular",
  padding = "",
  rounded = "rounded-lg",
  viewOnly,
}) => {
  const t = useTranslations("urlField");
  const filterRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  // The canonical value shown by the trigger — kept in local state so an
  // edit reflects immediately even when the parent stages it without
  // re-rendering a fresh `value` prop (e.g. the guestlist edit buffer).
  const [currentValue, setCurrentValue] = useState(value ?? "");
  // Raw passthrough for filter mode (a free-text search string).
  const [filterText, setFilterText] = useState(value ?? "");

  useEffect(() => {
    setCurrentValue(value ?? "");
    setFilterText(value ?? "");
  }, [value]);

  // --- Filter mode: a single free-text input (substring search) ----------
  if (isEditing && isSelected && isFilter) {
    return (
      <div className="flex flex-col flex-1">
        <FormInput
          ref={filterRef}
          id="filter-url"
          border={border}
          rounded={rounded}
          disabled={viewOnly}
          margin=""
          height={height}
          fontSize={fontSize}
          surface={surface}
          paddingInput={padding}
          value={filterText}
          onChangeAction={(v) => {
            setFilterText(v);
            onChangeValue(v);
          }}
          onEnterAction={() => filterRef.current?.blur()}
          onBlurAction={onBlurAction}
          onFocusAction={onFocusAction}
          width="w-full"
        />
      </div>
    );
  }

  // --- Edit mode: an input-like trigger that opens the editor modal ------
  if (isEditing && isSelected && editable) {
    const parsed = parseUrlField(currentValue);
    const displayText = parsed.label || parsed.url;
    return (
      <div className="flex flex-col flex-1">
        <button
          type="button"
          disabled={viewOnly}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
            if (onFocusAction) onFocusAction();
          }}
          className={`flex flex-row items-center gap-2 w-full ${height} ${border} ${rounded} ${surface} px-2 ${fontSize} outline-quaternary text-left disabled:opacity-50 disabled:cursor-default`}
        >
          <IconLink size={16} className="font-tertiary shrink-0" />
          <span className={`truncate ${displayText ? "" : "font-tertiary"}`}>
            {displayText || t("empty")}
          </span>
        </button>
        {error && <p className="mt-1 body-3-regular font-error">{error}</p>}
        {open && (
          <PUrlEditor
            value={currentValue}
            onCloseAction={() => {
              setOpen(false);
              if (onBlurAction) onBlurAction();
            }}
            onSaveAction={(v) => {
              setCurrentValue(v);
              onChangeValue(v);
              setOpen(false);
              if (onBlurAction) onBlurAction();
            }}
          />
        )}
      </div>
    );
  }

  // --- Read mode: a clickable link ---------------------------------------
  const parsed = parseUrlField(value);
  if (parsed.url && isSafeHttpUrl(parsed.url)) {
    return (
      <a
        href={parsed.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="font-focus hover:underline inline-block max-w-full truncate align-bottom"
      >
        {parsed.label || parsed.url}
      </a>
    );
  }
  // Non-URL / unsafe scheme → render as inert plain text, never a link.
  return <p className="truncate">{value}</p>;
};
